<template>
    <div>

        <BoxPaciente
            ref="BoxPaciente"
            :paciente="paciente"
        />

        <Buscar
            :data="data"
            :paciente="paciente"
            @handleSearch="handleSearch"
        />

        <rkm-table
            :fields="fields.tables.agendamento"
            :items="items"
            :tbody-tr-attr="handleRowAttr"
            ref="Table"
            sort-by="data"
            hide-filters
        >

            <template v-slot:cell(data)="row">
                {{ row.item.data.split("-").reverse().join("/") }}
            </template>

            <template v-slot:cell(incluido)="row">
                {{ getExames(row.item.incluido) }}
            </template>

            <template v-slot:cell(actions)="{ index, item }">
                <rkm-button
                    :id="uniqueId('show-btn')"
                    @click="$refs.Modal.open(item.agendamentoId)"
                    title="Detalhamento"
                    small
                >
                    <rkm-icon icon="search" />
                </rkm-button>

                <rkm-button
                    color="info"
                    small
                    title="Imprimir protocolo"
                    @click="handlePrint(item)"
                    :disabled="handleCancel(item)"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    color="warning"
                    small
                    class="colorButton"
                    title="Imprimir etiqueta"
                    @click="handleEtiquetaPrint(item)"
                    :disabled="handleCancel(item)"
                >
                    <rkm-icon icon="tags" />
                </rkm-button>

                <rkm-button
                    color="primary"
                    :title="item.regulacaoSolicitacaoId ? 'Solicitação Regulada, a edição deve ser feita no módulo correspondente' : 'Editar'"
                    small
                    @click.stop="handleAlter(item, index)"
                    :disabled="handleDisabledAlter(item)"
                >
                    <rkm-icon icon="pen" />
                </rkm-button>

                <rkm-button
                    title="Visualizar histórico"
                    color="success"
                    :id="uniqueId('show-btn')"
                    @click="$refs.ModalHistorico.open(item.agendamentoId)"
                    small
                >
                    <rkm-icon icon="history" />
                </rkm-button>

                <rkm-button
                    color="danger"
                    title="Cancelar"
                    small
                    :disabled="handleDisabledDestroy(item)"
                    @click.stop="openCancelamento(item)"
                >
                    <rkm-icon icon="trash" />
                </rkm-button>
            </template>
        </rkm-table>

        <rkm-row class="mt-2">
            <rkm-col>
                <p class="m-0">
                    Linhas em <span class="warning">amarelo</span> representam os agendamentos urgentes.
                </p>
                <p class="m-0">
                    Linhas em <span class="primary">azul</span> representam os agendamentos retroativos.
                </p>
                <p class="m-0">
                    Linhas em <span class="danger">vermelho</span> representam os agendamentos cancelados.
                </p>
            </rkm-col>
        </rkm-row>

        <Modal
            ref="Modal"
            :paciente="paciente"
            :data="data"
        />

        <rkm-modal
            ref="ModalCancelamento"
            title="Justificativa do cancelamento"
        >
            <rkm-form
                ref="FormCancelamento"
                show-legend
            >
                <rkm-form-textarea
                    label="Justificativa"
                    v-model="cancelamento.justificativa"
                    :validations="cancelamentoValidations.justificativa"
                    maxlength="1000"
                />
            </rkm-form>

            <template v-slot:footer="{ cancel }">
                <rkm-button
                    @click="cancel"
                >
                    Cancelar
                </rkm-button>

                <rkm-button
                    color="primary"
                    @click="confirmarCancelamento"
                >
                    Salvar
                </rkm-button>
            </template>
        </rkm-modal>

        <ModalHistorico
            ref="ModalHistorico"
            :paciente="paciente"
            :data="data"
        />

        <ModalPermissao
            ref="RkmModalPermissao"
            :origem="origem"
            @handlePermission="handlePermission"
        />
    </div>
</template>

<script>

// Vuex.
import { mapActions } from 'vuex'

// Moment.
import moment from 'moment'

// Mixins.
import { repository, error, object, data, stringCase, printExameLaboratorial } from '@/mixins'

// Components.
import Buscar from '@/components/exame-laboratorial/agendamento-exame/tabs/agendados/Form'
import Modal from '@/components/exame-laboratorial/agendamento-exame/tabs/agendados/components/Modal'
import ModalHistorico from '@/components/exame-laboratorial/agendamento-exame/tabs/agendados/components/ModalHistorico'
import BoxPaciente from '@/components/exame-laboratorial/agendamento-exame/paciente/Index'
import ModalPermissao from '@/components/exame-laboratorial/agendamento-exame/tabs/cadastro/components/permissao/Modal'

// Enums.
import preferenciaNome from '@/enums/cadastro/cidadao/preferenciaNome'
import statusAgendamento from '@/enums/exame-laboratorial/statusAgendamento'

// Filters.
import formats from '@/filters/formats'

export default {
    components:{ Buscar, Modal, ModalHistorico, BoxPaciente, ModalPermissao },
    computed:{
        cancelamentoValidations() {

            return {
                justificativa: {
                    required: true,
                    min: 3,
                    max: 1000,
                },
            }
        },
        fields() {

            return {
                tables: {
                    agendamento: [
                        { key: 'data', label: 'Data', sortable: true },
                        { key: 'horaInicial', label: 'Horário', class: 'col-min', sortable: true },
                        { key: 'agendamentoNumero', label: 'Agendamento', class: 'col-min', sortable: true },
                        { key: 'medicoNome', label: 'Médico solicitante', sortable: true },
                        { key: 'incluido', label: 'Exame(s)', sortable: true },
                        { key: 'unidadeNome', label: 'Unidade solicitante', sortable: true },
                        { key: 'infraestruturaNome', label: 'Local de atendimento', sortable: true },
                        { key: 'actions', label: 'Ações', class: 'col-min text-center'}
                    ]
                }
            }
        },
        items() {

            const agendamentos = this.search
                ? this.data.model.agendamentosSearch
                : this.data.model.agendamentos

            return agendamentos
        }
    },
    created() {

        this.repositories = {
            agendamento: this.getConectaSusRepository('exameLaboratorial.agendamentoExame'),
            impressao: this.getConectaSusRepository('impressao.exameLaboratorial'),
        }
    },
    data:() => ({
        cancelamento: {
            agendamentoId: null,
            justificativa: null,
        },
        agendamento: {
            data: null,
            infraestruturaId: null,
            infraestruturaNome: '',
            agendamentoId: '',
            unidadeId: null,
            unidadeNome: '',
            unidadeDisabled: false,
            usuarioId: '',
            periodo: {
                start: null,
                end: null,
            },
            medicoNome: '',
            medicoId: null,
        },
        origem: 'listagem',
        enums: { preferenciaNome, statusAgendamento },
        isReadOnly: false,
        search: false,
    }),
    filters: {
        ...formats,
    },
    mixins: [ repository, error, object, data, stringCase, printExameLaboratorial ],
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        validate() {
            return this.$refs.Form.validate()
        },
        openCancelamento(item) {

            this.cancelamento.agendamentoId = item.agendamentoId
            this.cancelamento.justificativa = null

            this.$refs.ModalCancelamento.show()
        },
        confirmarCancelamento() {

            this.$refs.FormCancelamento.validate().then(valid => {

                if (!valid) return

                this.setLoading()

                this.repositories.agendamento.cancel(this.cancelamento.agendamentoId, {
                    justificativa: this.cancelamento.justificativa,
                })
                    .then(() => {

                        this.marcarAgendamentoComoCancelado(this.cancelamento.agendamentoId)
                        this.showSaveNotification('Agendamento cancelado com sucesso!')
                        this.$refs.ModalCancelamento.hide()
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            })
        },
        marcarAgendamentoComoCancelado(agendamentoId) {

            this.data.model.agendamentos = this.data.model.agendamentos.map(obj => {
                if (obj.agendamentoId === agendamentoId) {
                    return {
                        ...obj,
                        incluido: obj.incluido.map(exame => ({
                            ...exame,
                            statusId: this.enums.statusAgendamento.CANCELADO,
                        }))
                    };
                }
                return obj;
            })

            this.data.model.agendamentosSearch = this.data.model.agendamentosSearch.map(obj => {
                if (obj.agendamentoId === agendamentoId) {
                    return {
                        ...obj,
                        incluido: obj.incluido.map(exame => ({
                            ...exame,
                            statusId: this.enums.statusAgendamento.CANCELADO,
                        }))
                    };
                }
                return obj;
            })
        },
        handleRowAttr(item, type) {
            if (type === 'row') {

                if (item.retroativo && !this.handleCancel(item)) {
                    return {
                        style: `
                            border-left: 10px solid #007bff;
                            background-color: #007bff17;
                        `
                    }
                } else if (item.urgente) {
                    return {
                        style: `
                            border-left: 10px solid #ffc107;
                            background-color: #ffc10715;
                        `
                    }
                } else if (this.handleCancel(item)) {
                    return {
                        style: `
                            border-left: 10px solid #dc3545;
                            background-color: #dc354515;
                        `
                    }
                }
            }
        },
        handleAlter(item, index) {

            if (!item.alterarAgendamento) {

                const confirm = async () => {

                    this.$refs.RkmModalPermissao.open(item, index)

                    return false
                }

                const msg = 'Já existe coleta lançada no agendamento, para continuar é necessário informar' +
                    ' os dados do supervisor. Deseja continuar?'

                this.showConfirmation(msg, confirm)

                return false
            } else if (item.isEmProcessamentoWebservice) {

                const confirm = async () => {

                    this.$refs.RkmModalPermissao.open(item, index)

                    return false
                }

                const msg = 'O agendamento está em processamento no sistema de terceiro (Web service), para continuar é ' +
                    'necessário informar os dados do supervisor. Deseja continuar?'

                this.showConfirmation(msg, confirm)

                return false
            } else {

                this.$emit('handleAlter', item, index)
            }
        },
        handlePermission(supervisorId, item, index){

            item.supervisorId = supervisorId

            this.$emit('handleAlter', item, index)
        },
        getExames(item){

            let nomeExames = ''

            if (item) {

                item.forEach(exame => {

                    if(!nomeExames){

                        nomeExames = exame.nome
                    } else {

                        nomeExames = nomeExames + ', ' + exame.nome
                    }
                })
            }

            return nomeExames
        },
        handleEtiquetaPrint(item) {

            this.setLoading()

            this.repositories.agendamento.printEtiqueta(item.agendamentoId)
                .then(({ data }) => {

                    this.print().etiqueta({
                        pacienteNome: this.paciente.nome,
                        pacienteNomeSocial: this.paciente.nomeSocial,
                        idade: this.paciente.anos > 1
                            ? `${this.paciente.anos} ANOS`
                            : `${this.paciente.anos} ANO` ,
                        prontuario: this.paciente.prontuario,
                        agendamentoNumero: data.agendamentoNumero,
                        unidadeNome: data.unidadeNome,
                        laboratorioNome: data.laboratorioNome,
                        sistemaNome: data.cliente.sistema.nome,
                        sistemaVersao: data.cliente.sistema.versao,
                        nomePrefeitura: data.unidade,
                    })
                })
            .catch(this.handleError)
            .finally(() => this.unsetLoading())
        },
        handlePrint(item) {

            let prazoEntrega = null
            let dataEntrega = null

            this.setLoading()

            this.repositories.agendamento.printProtocolo(item.agendamentoId)
                .then(({ data }) => {

                    data.incluido.forEach(exame => {

                        if (exame.prazoEntrega !== null) {

                            if (prazoEntrega == null) {
                                prazoEntrega = exame.prazoEntrega
                            } else {

                                if (prazoEntrega < exame.prazoEntrega) {
                                    prazoEntrega = exame.prazoEntrega
                                }
                            }
                        }
                    })

                    dataEntrega = prazoEntrega !== null
                        ? moment(data.data).add(prazoEntrega, 'days').format('DD/MM/YYYY')
                        : ''

                    this.print().agendamento({
                        pacienteNome: this.paciente.nome,
                        pacienteNomeSocial: this.paciente.nomeSocial,
                        prontuario: this.paciente.prontuario,
                        cpf: formats.formatCpf(this.paciente.cpf),
                        nomeResponsavel: data.responsavel,
                        dataNascimento: this.paciente.dataNascimento.split("-").reverse().join("/"),
                        agendamentoNumero: data.agendamentoNumero,
                        regulacaoSolicitacaoNumero: data.regulacaoSolicitacaoNumero,
                        medicoNome: data.medicoNome,
                        grupoExameNome: data.grupoExameNome,
                        observacao: data.observacao,
                        data: moment(data.data).format('DD/MM/YYYY'),
                        examesIncluido: data.incluido,
                        horaExame: data.horaInicial,
                        unidade: data.unidadeNome,
                        endereco: data.unidadeEndereco,
                        numeroEndereco: data.unidadeNumeroEndereco,
                        bairro: data.unidadeBairro,
                        telefone: data.unidadeTelefone,
                        tipo: data.unidadeTipo,
                        cep: data.unidadeCep,
                        localColeta: data.coletaNome,
                        enderecoColeta: data.coletaEndereco,
                        numeroEnderecoColeta: data.coletaNumeroEndereco,
                        bairroColeta: data.coletaBairro,
                        telefoneColeta: data.coletaTelefone,
                        tipoColeta: data.coletaTipo,
                        cepColeta: data.coletaCep,
                        laboratorio: data.laboratorioNome,
                        enderecoLaboratorio: data.laboratorioEndereco,
                        numeroEnderecoLaboratorio: data.laboratorioNumeroEndereco,
                        bairroLaboratorio: data.laboratorioBairro,
                        telefoneLaboratorio: data.laboratorioTelefone,
                        tipoLaboratorio: data.laboratorioTipo,
                        cepLaboratorio: data.laboratorioCep,
                        logotipo: data.cliente.logotipo,
                        nomePrefeitura: data.cliente.nome,
                        ramoAtividade: data.cliente.sistema.ramoAtividade.nome,
                        mensagemProtocolo: data.mensagemProtocolo,
                        sistemaNome: data.cliente.sistema.nome,
                        sistemaVersao: data.cliente.sistema.versao,
                        usuario: data.usuario,
                        usuarioNomeSocial: data.usuarioNomeSocial ? data.usuarioNomeSocial : null,
                        urgente: data.urgente,
                        dataEntrega: dataEntrega,
                    })
                })
            .catch(this.handleError)
            .finally(() => this.unsetLoading())
        },
        handleSearch(value) {

            this.search = value
        },
        handleCancel(item) {

            return item.incluido.every(i => i.statusId === this.enums.statusAgendamento.CANCELADO)
        },
        isItemRegulado(item) {

            return item.regulacaoSolicitacaoId ? true : false;
        },
        handleUnidadeLogada(item) {

            return !this.data.form.isSupervisor && item.infraestruturaId != this.data.form.infraestruturaId ? true : false;
        },
        handleDisabledDestroy(item) {

            return (item.excluirAgendamento || item.retroativo || this.handleCancel(item) || this.isItemRegulado(item)
                || this.handleUnidadeLogada(item)) ? true : false
        },
        handleDisabledAlter(item) {

            return (item.retroativo || this.isItemRegulado(item) || this.handleCancel(item)
                || this.handleUnidadeLogada(item)) ? true : false;
        }
    },
    props: {
        data: { type: Object, required: true },
        paciente: { type: Object, required: true},
    },
}
</script>

<style scoped>
p {
    margin: 0px;
}
.col-svg {
    text-align: center !important;
}
.icon {
    width: auto !important;
    height: 96px !important;
    color: #007bff !important;
}
.warning {
    color: var(--warning);
}
.primary {
    color: var(--primary);
}
.danger {
    color: var(--danger);
}
.colorButton {
    color: #fff;
}
</style>